import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

import { ANIM, HOME, HOME_MID, SPACE } from '../../../../theme/tokens';
import { cardEnterAt, fadeInWhenReady } from '../../../../theme/mixins';
import { GlowCard, Text } from '../../../../components';
import { Card } from '../AcquisitionChannels/AcquisitionChannels.styles';

/**
 * "Top marketing assets" list. Desktop: one compact row per asset (thumbnail, name + kind, the landing page it goes
 * through, the three figures, the open button), each row in its own container, the Acquisition Channels card (its
 * surface, radius, padding and hover come from `Card`), under one column-label row aligned to the cards' insides.
 * Phones (HOME_MID): the row collapses to the thumbnail beside three lines (name + kind, the landing page, the figures
 * with their own labels) and the label row hides.
 *
 * Every line has a fixed height so a skeleton row and a loaded row are the same size (no shift on reveal).
 */

/** Thumbnail edge (px). Also the skeleton's. */
export const THUMB_PX = 48;

/** The open button's column: the channel card's chevron (24px). */
const CHEV_PX = 24;

const columns = css`
  display: grid;
  grid-template-columns: ${THUMB_PX}px minmax(0, 1fr) minmax(0, 0.7fr) 104px 72px 80px 120px ${CHEV_PX}px;
  grid-template-areas: 'thumb name via reg ftd dcount dep chev';
  align-items: center;
  column-gap: ${SPACE[16]};
`;

/** The cards, spaced as the channel cards are. */
export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
`;

/** The column labels, inset by the card's padding so they sit over the cards' columns. */
export const LabelRow = styled.div`
  ${columns}
  padding: ${SPACE[4]} ${SPACE[12]} ${SPACE[8]};

  ${HOME_MID} {
    display: none;
  }
`;

/** A column label; `$fromThumb` starts it at the thumbnail column (the "Asset" label lines up with the images). */
export const ColLabel = styled(Text)<{ $area: string; $end?: boolean; $fromThumb?: boolean }>`
  grid-area: ${(p) => p.$area};
  ${(p) => (p.$fromThumb ? 'grid-column: thumb-start / name-end;' : '')}
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  color: ${HOME.t500};
  text-align: ${(p) => (p.$end ? 'right' : 'left')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** One asset: the channel card, laid out as one row. */
export const Row = styled(Card)<{ $index: number; $enter: boolean }>`
  ${columns}

  ${(p) => (p.$enter ? cardEnterAt(p.$index, 'backwards') : '')}

  ${HOME_MID} {
    grid-template-columns: ${THUMB_PX}px minmax(0, 1fr) ${CHEV_PX}px;
    grid-template-areas:
      'thumb name chev'
      'thumb via via'
      'thumb figs figs';
    align-items: start;
    row-gap: ${SPACE[4]};
  }
`;

export const Thumb = styled.div`
  grid-area: thumb;
  position: relative;
  width: ${THUMB_PX}px;
  height: ${THUMB_PX}px;
  border-radius: 10px;
  overflow: hidden;
  display: grid;
  place-items: center;
  background: ${HOME.panel};
  color: ${HOME.t400};
`;

export const ThumbImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  ${fadeInWhenReady}
`;

export const NameCell = styled.div`
  grid-area: name;
  min-width: 0;
`;

const lineEllipsis = css`
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
`;

export const NameLine = styled.div`
  ${lineEllipsis}
  height: 20px;
  font-size: 15px;
  font-weight: 600;
  color: ${HOME.t50};
`;

const quietLink = css`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: inherit;
  text-decoration: underline;
  text-decoration-color: transparent;
  text-underline-offset: 3px;
  transition: text-decoration-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover,
  &:focus-visible {
    color: inherit;
    text-decoration-color: currentColor;
  }
`;

export const NameLink = styled(Link)`
  ${quietLink}
`;

export const PlainName = styled(Text)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const KindLine = styled.div`
  ${lineEllipsis}
  gap: ${SPACE[4]};
  height: 16px;
  margin-top: ${SPACE[2]};
  font-size: 13px;
  font-weight: 400;
  color: ${HOME.t400};
`;

export const ViaCell = styled.div`
  ${lineEllipsis}
  grid-area: via;
  gap: ${SPACE[4]};
  height: 18px;
  font-size: 14px;
  font-weight: 500;
  color: ${HOME.t100};
`;

/** "via", only on phones (the desktop label row names the column). */
export const ViaPrefix = styled(Text)`
  display: none;
  flex-shrink: 0;
  color: ${HOME.t500};
  font-weight: 400;

  ${HOME_MID} {
    display: inline;
  }
`;

export const ViaLink = styled(Link)`
  ${quietLink}
`;

export const Muted = styled(Text)`
  color: ${HOME.t500};
  font-weight: 400;
`;

export const More = styled(Text)`
  flex-shrink: 0;
  color: ${HOME.t400};
  font-size: 12px;
  font-weight: 500;
`;

/** The three figures: grid cells of the row on desktop, one wrapped line of their own on phones. */
export const Figures = styled.div`
  display: contents;

  ${HOME_MID} {
    grid-area: figs;
    display: flex;
    flex-wrap: wrap;
    column-gap: ${SPACE[16]};
    row-gap: ${SPACE[2]};
  }
`;

export const Fig = styled.div<{ $area: string }>`
  grid-area: ${(p) => p.$area};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${SPACE[4]};
  height: 22px;
  min-width: 0;

  ${HOME_MID} {
    grid-area: auto;
    justify-content: flex-start;
  }
`;

export const FigValue = styled.span`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  color: ${HOME.t300};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

/** The figure's own label, only on phones. */
export const FigLabel = styled(Text)`
  display: none;
  font-size: 13px;
  font-weight: 300;
  color: ${HOME.t400};
  white-space: nowrap;

  ${HOME_MID} {
    display: inline;
  }
`;

/** The open button's cell (the channel card's chevron sits in it). */
export const ChevCell = styled.div`
  grid-area: chev;
  display: flex;
  justify-content: flex-end;
  align-self: center;

  ${HOME_MID} {
    align-self: start;
  }
`;

export const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${SPACE[12]} ${SPACE[12]} ${SPACE[4]};
`;

const actionLink = css`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  font-size: 14px;
  font-weight: 600;
  color: ${HOME.t300};
  text-decoration: none;
  transition: color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover,
  &:focus-visible {
    color: ${HOME.t50};
  }
`;

export const FooterLink = styled(Link)`
  ${actionLink}
`;

/** The empty state: a faint preview of the rows that will rank here, beside what to do about it. */
export const EmptyStage = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
  align-items: center;
  gap: ${SPACE[24]};
  padding: ${SPACE[8]} ${SPACE[4]} ${SPACE[4]};

  ${HOME_MID} {
    grid-template-columns: minmax(0, 1fr);
    gap: ${SPACE[16]};
  }
`;

export const EmptyCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACE[8]};
  min-width: 0;
`;

export const EmptyTitle = styled(Text)`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
  color: ${HOME.t100};
`;

export const EmptyText = styled(Text)`
  margin: 0;
  max-width: 420px;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.45;
  color: ${HOME.t400};
`;

/** The empty state's action: a quiet pill, the Home pills' surface. */
export const EmptyCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  margin-top: ${SPACE[8]};
  padding: ${SPACE[8]} ${SPACE[16]};
  border-radius: 999px;
  border: 1px solid ${HOME.hairline};
  background: ${HOME.panel};
  color: ${HOME.t100};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover,
  &:focus-visible {
    border-color: ${HOME.t500};
  }
`;

/** The no-assets state's shortcuts under the action (Image Banners, Video Ads), one per row: the copy column is too
 *  narrow on desktop for two side by side, and a lone one keeps the full width. */
export const Shortcuts = styled.ul`
  list-style: none;
  width: 100%;
  margin: ${SPACE[8]} 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

/** The shortcut's link, stretched over the whole card (the card's own control: its keyboard focus rings the card). */
export const ShortcutLink = styled(Link)`
  color: ${HOME.t100};
  text-decoration: none;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }

  &:hover,
  &:focus {
    color: ${HOME.t100};
    text-decoration: none;
  }
`;

/** A shortcut into a Marketing area's library: a GlowCard in the area's colours, as its Marketing hub card is, at a
 *  row's radius (the ghost rows' size). */
export const Shortcut = styled(GlowCard)`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  min-width: 0;
  padding: ${SPACE[12]};
  border-radius: 12px;

  &:focus-within:has(${ShortcutLink}:focus-visible) {
    outline: 2px solid ${HOME.t100};
    outline-offset: 2px;
  }
`;

/** The shortcut's glyph tile, the ghost rows' tile. */
export const ShortcutTile = styled.div`
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: ${HOME.hairline};
`;

export const ShortcutWords = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
`;

export const ShortcutText = styled(Text)`
  font-size: 12px;
  font-weight: 300;
  line-height: 1.4;
  color: ${HOME.t400};
`;

/** Decoration: clicks pass through to the stretched link (its hover nudge would otherwise lift it above the link). */
export const ShortcutArrow = styled.span`
  flex: none;
  margin-left: auto;
  display: grid;
  place-items: center;
  pointer-events: none;
  transition: transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  ${Shortcut}:hover & {
    transform: translateX(2px);
  }
`;

/** The preview: three ghost rows (the kinds that can rank), each fainter than the one above. */
export const GhostList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  pointer-events: none;
  user-select: none;
`;

const GHOST_OPACITY = [0.75, 0.45, 0.2];

/** A ghost row in its area's colours: a GlowCard, as the Marketing hub card for that kind of asset is, at a row's
 *  smaller radius (a touch of colour). */
export const GhostRow = styled(GlowCard)<{ $depth: number }>`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  padding: ${SPACE[8]};
  border-radius: 12px;
  opacity: ${(p) => GHOST_OPACITY[p.$depth] ?? 0.2};
`;

export const GhostThumb = styled.div`
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: ${HOME.hairline};
  color: ${HOME.t400};
`;

export const GhostLines = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  min-width: 0;
`;

export const GhostBar = styled.span<{ $w: string }>`
  display: block;
  width: ${(p) => p.$w};
  height: ${SPACE[8]};
  border-radius: 4px;
  background: ${HOME.hairline};
`;

export const GhostFigures = styled.div`
  flex: none;
  display: flex;
  gap: ${SPACE[16]};
  padding-right: ${SPACE[8]};
`;

export const Quiet = styled(Text)`
  display: block;
  margin: 0;
  padding: ${SPACE[8]} ${SPACE[8]} ${SPACE[4]};
  font-size: 14px;
  font-weight: 300;
  color: ${HOME.t500};
`;

export { Chev } from '../AcquisitionChannels/AcquisitionChannels.styles';
